import React, { useState, useEffect } from 'react';
import type { BusinessHoursFormData } from '../types/businessHours.types';
import { businessHoursService } from '../services/businessHoursService';
import { toast } from 'react-hot-toast';
import { logger } from '../utils/logger'

interface BusinessHoursConfigProps {
  botId: number;
  onClose: () => void;
}

const BusinessHoursConfig: React.FC<BusinessHoursConfigProps> = ({ botId, onClose }) => {
  const [formData, setFormData] = useState<BusinessHoursFormData>({
    is_enabled: false,
    timezone: 'America/Campo_Grande',
    monday_start: '09:00:00',
    monday_end: '18:00:00',
    monday_enabled: true,
    tuesday_start: '09:00:00',
    tuesday_end: '18:00:00',
    tuesday_enabled: true,
    wednesday_start: '09:00:00',
    wednesday_end: '18:00:00',
    wednesday_enabled: true,
    thursday_start: '09:00:00',
    thursday_end: '18:00:00',
    thursday_enabled: true,
    friday_start: '09:00:00',
    friday_end: '18:00:00',
    friday_enabled: true,
    saturday_start: '09:00:00',
    saturday_end: '18:00:00',
    saturday_enabled: false,
    sunday_start: '09:00:00',
    sunday_end: '18:00:00',
    sunday_enabled: false,
    outside_hours_message: 'Estamos fora do horário comercial. Retornaremos em breve!'
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const days = [
    { key: 'monday', label: 'Segunda-feira' },
    { key: 'tuesday', label: 'Terça-feira' },
    { key: 'wednesday', label: 'Quarta-feira' },
    { key: 'thursday', label: 'Quinta-feira' },
    { key: 'friday', label: 'Sexta-feira' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ];

  const timezones = [
    'America/Campo_Grande',
    'America/Sao_Paulo',
    'America/Manaus',
    'America/Recife',
    'America/Bahia',
    'America/Fortaleza',
    'America/Cuiaba',
    'America/Porto_Velho',
    'America/Acre',
    'America/Rio_Branco'
  ];

  useEffect(() => {
    loadBusinessHours();
  }, [botId]);

  const loadBusinessHours = async () => {
    try {
      setLoading(true);
      const config = await businessHoursService.getBusinessHours(botId);
      setFormData(config);
    } catch (error) {
      logger.error('Erro ao carregar configuração de horário comercial:', error);
      toast.error('Erro ao carregar configuração de horário comercial');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BusinessHoursFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayToggle = (day: string) => {
    const enabledField = `${day}_enabled` as keyof BusinessHoursFormData;
    handleInputChange(enabledField, !formData[enabledField]);
  };

  const handleSave = async () => {
    try {
      if (formData.is_enabled && !formData.outside_hours_message.trim()) {
        toast.error('Mensagem de ausência é obrigatória quando horário comercial está habilitado');
        return;
      }

      setSaving(true);
      await businessHoursService.saveBusinessHours(botId, formData);
      toast.success('Configuração de horário comercial salva com sucesso!');
      onClose();
    } catch (error) {
      logger.error('Erro ao salvar configuração de horário comercial:', error);
      toast.error('Erro ao salvar configuração de horário comercial');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Carregando configuração...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Configuração de Horário Comercial</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Ativar/Desativar */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_enabled"
              checked={formData.is_enabled}
              onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_enabled" className="text-sm font-medium text-gray-700">
              Ativar horário comercial
            </label>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fuso Horário
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Mensagem de Ausência */}
          {formData.is_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de Ausência *
              </label>
              <textarea
                value={formData.outside_hours_message}
                onChange={(e) => handleInputChange('outside_hours_message', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite a mensagem que será enviada quando estiver fora do horário comercial"
              />
            </div>
          )}

          {/* Configuração dos Dias */}
          {formData.is_enabled && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Horários por Dia da Semana</h3>
              <div className="space-y-4">
                {days.map(day => (
                  <div key={day.key} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`${day.key}_enabled`}
                        checked={formData[`${day.key}_enabled` as keyof BusinessHoursFormData] as boolean}
                        onChange={() => handleDayToggle(day.key)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`${day.key}_enabled`} className="text-sm font-medium text-gray-700 w-24">
                        {day.label}
                      </label>
                    </div>

                    {formData[`${day.key}_enabled` as keyof BusinessHoursFormData] && (
                      <div className="flex items-center space-x-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Início</label>
                          <input
                            type="time"
                            value={formData[`${day.key}_start` as keyof BusinessHoursFormData] as string}
                            onChange={(e) => handleInputChange(`${day.key}_start` as keyof BusinessHoursFormData, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <span className="text-gray-400">até</span>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Fim</label>
                          <input
                            type="time"
                            value={formData[`${day.key}_end` as keyof BusinessHoursFormData] as string}
                            onChange={(e) => handleInputChange(`${day.key}_end` as keyof BusinessHoursFormData, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessHoursConfig;
